import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useConfirm } from "../context/ConfirmContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { KeyRound, Plus, Trash2, ToggleLeft, ToggleRight } from "lucide-react";
import { toast } from "sonner";

export default function AccessPinsAdmin() {
  const confirm = useConfirm();
  const [pins, setPins] = useState([]);
  const [newPin, setNewPin] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newMonths, setNewMonths] = useState(1);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/admin/access-pins").then((r) => setPins(r.data || [])).catch(() => {});

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newPin.trim()) return toast.error("Captura un PIN");
    setBusy(true);
    try {
      await api.post("/admin/access-pins", { pin: newPin.trim(), label: newLabel.trim(), duration_months: newMonths });
      toast.success("PIN creado");
      setNewPin("");
      setNewLabel("");
      setNewMonths(1);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear PIN");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (p) => {
    try {
      const { data } = await api.patch(`/admin/access-pins/${p.id}/toggle`);
      toast.success(data.is_active ? "PIN activado" : "PIN desactivado");
      load();
    } catch (e) {
      toast.error("Error al cambiar estado");
    }
  };

  const remove = async (p) => {
    if (!await confirm(`¿Eliminar el PIN "${p.pin}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/access-pins/${p.id}`);
      toast.success("PIN eliminado");
      load();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  return (
    <div className="space-y-6">
      {/* Create */}
      <div className="bg-white border border-border rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="w-5 h-5 text-[hsl(var(--primary))]" />
          <h3 className="font-display text-2xl tracking-tight">Nuevo código de acceso</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Los comercios que usen este código al registrarse obtienen suscripción activa de forma automática, sin pagar.
        </p>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label className="text-sm">PIN / código *</Label>
            <Input
              value={newPin}
              onChange={(e) => setNewPin(e.target.value)}
              placeholder="Ej. GRATIS2025"
              data-testid="pin-new-value"
            />
          </div>
          <div>
            <Label className="text-sm">Etiqueta (para tu referencia)</Label>
            <Input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Ej. Campaña Enero, Demo clientes"
              data-testid="pin-new-label"
            />
          </div>
          <div>
            <Label className="text-sm">Duración (meses)</Label>
            <select
              value={newMonths}
              onChange={(e) => setNewMonths(Number(e.target.value))}
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              data-testid="pin-new-months"
            >
              {[1,2,3,6,12,24].map(m => (
                <option key={m} value={m}>{m} {m === 1 ? "mes" : "meses"}</option>
              ))}
            </select>
          </div>
        </div>
        <Button onClick={create} disabled={busy} className="mt-4 btn-primary rounded-full px-6" data-testid="pin-create-btn">
          <Plus className="w-4 h-4 mr-2" />
          {busy ? "Creando…" : "Crear PIN"}
        </Button>
      </div>

      {/* List */}
      <div className="bg-white border border-border rounded-2xl p-6">
        <h3 className="font-display text-xl tracking-tight mb-4">PINs existentes ({pins.length})</h3>
        {pins.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-10 border-2 border-dashed border-border rounded-xl" data-testid="pins-empty">
            No hay códigos creados aún.
          </div>
        ) : (
          <div className="space-y-3">
            {pins.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-4 border border-border rounded-xl px-4 py-3" data-testid={`pin-row-${p.id}`}>
                <div className="flex items-center gap-3">
                  <code className="text-base font-bold tracking-wider bg-zinc-100 px-2 py-0.5 rounded">{p.pin}</code>
                  {p.label && <span className="text-sm text-muted-foreground">{p.label}</span>}
                  <span className="text-xs text-muted-foreground bg-zinc-100 px-2 py-0.5 rounded">
                    {p.duration_months ?? 1} {(p.duration_months ?? 1) === 1 ? "mes" : "meses"}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground">Usos: {p.used_count ?? 0}</span>
                  <Badge className={p.is_active ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-500"}>
                    {p.is_active ? "Activo" : "Inactivo"}
                  </Badge>
                  <button
                    onClick={() => toggle(p)}
                    className="text-muted-foreground hover:text-zinc-900"
                    title={p.is_active ? "Desactivar" : "Activar"}
                    data-testid={`pin-toggle-${p.id}`}
                  >
                    {p.is_active ? <ToggleRight className="w-5 h-5 text-emerald-600" /> : <ToggleLeft className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => remove(p)}
                    className="text-rose-400 hover:text-rose-600"
                    title="Eliminar"
                    data-testid={`pin-delete-${p.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
